import { useQueryClient } from '@tanstack/react-query';
import { CircleStop } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useApi } from '../api/context';
import { queryKeys } from '../api/queries';
import type { Run, RunKind } from '../api/types';
import { useToast } from '../components/toast';
import { ActionButton, Button } from '../components/ui';
import { strings } from '../strings';

const s = strings.runPage;

/**
 * Cancel run… with its confirmation popover (spec 6.5, design 9.3): a non-modal dialog that says what a
 * force-stop means; Keep running or Esc closes it. After `cancelRequested` the button is disabled.
 */
export function CancelRunButton({
  run,
  label = s.cancel,
  small,
}: {
  run: Pick<Run, 'id' | 'kind' | 'cancelRequested' | 'deploymentId'> & { kind: RunKind };
  label?: string;
  small?: boolean;
}) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(false);
  const titleId = useId();
  const wrapper = useRef<HTMLDivElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const cancelRequested = run.cancelRequested || requested;

  useEffect(() => {
    if (!open) return;
    popover.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    wrapper.current?.querySelector<HTMLButtonElement>('button')?.focus();
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const updated = await api.cancelRun(run.id);
      setRequested(true);
      queryClient.setQueryData(queryKeys.run(run.id), updated);
      void queryClient.invalidateQueries({ queryKey: ['deployment', run.deploymentId] });
      void queryClient.invalidateQueries({ queryKey: ['runs'] });
      close();
    } catch (error) {
      toast.error(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={wrapper} className="relative inline-block">
      <ActionButton
        permission="operate"
        variant="danger"
        className={small ? 'btn-sm' : undefined}
        disabledReason={cancelRequested ? s.cancelRequested : null}
        icon={<CircleStop aria-hidden className="size-4" />}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {cancelRequested ? s.cancelRequested : label}
      </ActionButton>
      {open && (
        <div
          ref={popover}
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          className="popover cancel-popover"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              close();
            }
          }}
        >
          <h2 id={titleId} className="mb-1 flex items-center gap-2 text-sm font-semibold">
            <CircleStop aria-hidden className="size-4 text-danger" />
            {s.cancelTitle(strings.status.runKind[run.kind])}
          </h2>
          <p className="text-sm">{s.cancelText}</p>
          <div className="mt-3 flex justify-end gap-2">
            <Button onClick={close}>{s.keepRunning}</Button>
            <Button variant="danger" disabled={busy} onClick={() => void cancel()}>
              {s.confirmCancel}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
